// FormDesvinculaciones.jsx
import React, { useMemo, useState, useEffect, useRef, useImperativeHandle } from "react";
import { notify } from "../../ui/notify";
import {
  Form,
  Input,
  Select,
  Upload,
  Typography,
  Row,
  Col,
  Alert,
  Button,
  Spin,
  Tag,
  Space,
} from "antd";
import ScrollDatePicker from "../../calendar/ScrollDatePicker";

import {
  InboxOutlined,
  CalendarOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import ModalNuevoCliente from "../../modals/desvinculaciones/ModalNuevoCliente";

import {
  actionDesvinculacionCreate,
  actionDesvinculacionUpdateById,
} from "../../../redux/actions/desvinculaciones/desvinculaciones";

import axios from "axios";
import {PATH} from "../../../redux/utils"
import "../tribunal/FormTribunal.css";
const { Dragger } = Upload;
const { Text, Title } = Typography;
const API_BASE = PATH;

const apiServiceGet = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});


const authHeader = () => {
  const token = localStorage.getItem("tokenadmin") || localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/* ========= Utils ========= */
const collapseSpaces = (s) =>
  String(s ?? "")
    .normalize("NFC")
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const normLabel = (txt) => collapseSpaces(txt || "").trim().toLowerCase();

const toNullIfEmptyEffective = (v) => {
  if (v === undefined || v === null) return null;
  if (typeof v !== "string") return v;
  return v.replace(/\s/g, "") === "" ? null : v;
};

const sanitizeByField = (name, v) => {
  const s = collapseSpaces(v);
  switch (name) {
    case "solicitante":
    case "empresa_nombre":
      return s.replace(/[^A-Za-zÀ-ÿ'. -]/g, "").slice(0, 120);
    case "domicilio_ejecucion":
    case "notas":
      return s.replace(/[\u0000-\u001F\u007F]/g, "").slice(0, 1000);
    default:
      return s;
  }
};

// Igual que sanitizeByField pero sin trim, para usarse en onChange
// y no cortar el espacio que el usuario acaba de escribir
const sanitizeByFieldLive = (name, v) => {
  const s = String(v ?? "")
    .normalize("NFC")
    .replace(/\s+/g, " ");
  switch (name) {
    case "solicitante":
    case "empresa_nombre":
      return s.replace(/[^A-Za-zÀ-ÿ'.\- ]/g, "").slice(0, 120);
    case "domicilio_ejecucion":
    case "notas":
      return s.slice(0, 500);
    default:
      return s;
  }
};

const normalizeFechaDDMMYYYYHHMM_to_YYYYMMDD_HHMMSS = (str) => {
  if (!str) return null;

  const s = String(str).trim();
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?$/);
  if (!m) return null;

  const dd = m[1];
  const mm = m[2];
  const yyyy = m[3];
  const HH = m[4] ?? "07";
  const MM = m[5] ?? "00";

  return `${yyyy}-${mm}-${dd} ${HH}:${MM}:00`;
};


const normalizeFechaDDMMYYYY_to_YYYYMMDD = (str) => {
  if (!str) return null;
  const digits = String(str).replace(/\D/g, "");
  if (digits.length !== 8) return null;
  const dd = digits.substring(0, 2);
  const mm = digits.substring(2, 4);
  const yyyy = digits.substring(4, 8);
  return `${yyyy}-${mm}-${dd}`;
};

const isoToDDMMYYYY = (v) => {
  if (!v) return "";
  const d = dayjs(v);
  return d.isValid() ? d.format("DD/MM/YYYY") : "";
};

const dedupRazones = (arr = []) => {
  const map = new Map();
  for (const r of arr) {
    const key = `${normLabel(r.label)}|${r.empresa_id ?? "null"}`;
    const existing = map.get(key);
    if (!existing) map.set(key, r);
    else if (existing.value == null && r.value != null) map.set(key, r);
  }
  return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, "es"));
};

const pickCompanyName = (it) =>
  it?.nombre ??
  it?.nombre_cliente ??
  it?.empresa ??
  it?.name ??
  it?.razon_social ??
  it?.empresa_nombre ??
  "";

const coerceBool01 = (v, defaultVal = true) => {
  if (v === undefined || v === null || v === "") return defaultVal;
  const s = String(v).trim().toLowerCase();
  if (s === "1" || s === "true" || s === "si" || s === "sí") return true;
  if (s === "0" || s === "false" || s === "no") return false;
  return Boolean(v);
};

const pickFirst = (obj, keys) => {
  for (const k of keys) {
    const val = obj?.[k];
    const clean = collapseSpaces(val || "");
    if (clean) return clean;
  }
  return "";
};

const coerceIntOrNull = (v) => {
  if (v === undefined || v === null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const FormDesvinculaciones = React.forwardRef(
  (
    {
      variant = "context_view",
      initialValues = {},
      onSaved,
      idCiudad,
      ctxEmpresaId,
      onCancel,
      isEdit = false,
      idDesvinculacion = null,
      showTopActions = true,
      ctxRazonSocialIds,
      onSubmit = null,
      id_empresa,
      catalogEstadoOptions = [],
      catalogCiudadOptions = [],
      catalogEmpresaNombreOptionsBase = [],
      catalogEmpresasIndex = new Map(),
      catalogEmpresasItems = [],
    },
    ref
  ) => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [form] = Form.useForm();

    const [submitting, setSubmitting] = useState(false);
    const [openNuevoCliente, setOpenNuevoCliente] = useState(false);

    const [modalidadBajaOptions, setModalidadBajaOptions] = useState([]);
    const [lugarEjecucionOptions, setLugarEjecucionOptions] = useState([]);

    useEffect(() => {
      const fetchCatalogs = async () => {
        try {
          const [mRes, lRes] = await Promise.all([
            apiServiceGet.get("catalogos/modalidad_baja", { headers: authHeader() }),
            apiServiceGet.get("catalogos/lugar_ejecucion", { headers: authHeader() }),
          ]);
          const mData = Array.isArray(mRes.data) ? mRes.data : (mRes.data?.data ?? []);
          const lData = Array.isArray(lRes.data) ? lRes.data : (lRes.data?.data ?? []);
          setModalidadBajaOptions(
            mData
              .filter((d) => d.active == null || d.active)
              .map((d) => ({ value: d.id_modalidad_baja, label: d.nombre }))
          );
          setLugarEjecucionOptions(
            lData
              .filter((d) => d.active == null || d.active)
              .map((d) => ({ value: d.id_lugar_ejecucion, label: d.nombre }))
          );
        } catch (e) {
          console.error("Error cargando catálogos lugar/modalidad:", e);
        }
      };
      fetchCatalogs();
    }, []);

    const [empleadosCount, setEmpleadosCount] = useState(1);
    const empleadosWatch = Form.useWatch("empleados", form);

    const isStandaloneFull = variant === "standalone_full";
// ============================
// NUEVO: Estado/Ciudad (como Centro Conciliación)
// ============================
const [selectedEstadoId, setSelectedEstadoId] = useState(null);

const ciudadesById = useMemo(() => {
  const map = new Map();
  (catalogCiudadOptions || []).forEach((c) => {
    if (c?.value != null) map.set(Number(c.value), c);
  });
  return map;
}, [catalogCiudadOptions]);

const ciudadOptionsFiltered = useMemo(() => {
  const eid = Number(selectedEstadoId);
  if (!Number.isFinite(eid)) return [];

  return (catalogCiudadOptions || [])
    .filter((c) => Number(c.id_estado) === eid)
    .map((c) => ({
      label: c.label,
      value: c.value,
    }));
}, [catalogCiudadOptions, selectedEstadoId]);


// Si ya hay ciudad (por edit o por idCiudad), deduce el estado automáticamente.
useEffect(() => {
  if (!isStandaloneFull) return;

  const ciudadVal = form.getFieldValue("ciudad");
  const cid = ciudadVal != null ? Number(ciudadVal) : null;
  if (!Number.isFinite(cid)) return;

  const city = ciudadesById.get(cid);
  const eid = city?.id_estado != null ? Number(city.id_estado) : null;
  if (!Number.isFinite(eid)) return;

  setSelectedEstadoId(eid);
}, [isStandaloneFull, form, ciudadesById]);
// Cuando cambia estado, si la ciudad actual no es de ese estado, limpia ciudad.
useEffect(() => {
  if (!isStandaloneFull) return;

  const eid = Number(selectedEstadoId);
  if (!Number.isFinite(eid)) return;


  const ciudadVal = form.getFieldValue("ciudad");
  const cid = ciudadVal != null ? Number(ciudadVal) : null;

  if (Number.isFinite(cid)) {
    const city = ciudadesById.get(cid);
    if (Number(city?.id_estado) === eid) return;
  }

  form.setFieldsValue({ ciudad: undefined });
}, [isStandaloneFull, selectedEstadoId, form, ciudadesById]);

    // ========= NUEVO: origen del cliente =========
    const [clienteSource, setClienteSource] = useState("search"); // "search" | "modal"
    const [nuevoClienteData, setNuevoClienteData] = useState(null); // payload del modal

    const isClienteDesdeModal = clienteSource === "modal" && !!nuevoClienteData;

    // Documentos (varios)
    const [documentsMap, setDocumentsMap] = useState({ documento: [] });
    const [docErrors, setDocErrors] = useState({});

    // ===== Refs para Enter->Next =====
    const solicitanteRef = useRef(null);
    const fechaEncargoRef = useRef(null);
    const fechaSolicitudRef = useRef(null);
    const viaRef = useRef(null);
    const notasRef = useRef(null);

    const ciudadRef = useRef(null);
    const empresaRef = useRef(null);
    const razonRef = useRef(null);

    const modalidadBajaRef = useRef(null);
    const lugarEjecucionRef = useRef(null);
    const domicilioRef = useRef(null);

    const focusOrder = useMemo(() => {
      if (isStandaloneFull) {
        return [
          ciudadRef,
          empresaRef,
          razonRef,
          solicitanteRef,
          fechaEncargoRef,
          fechaSolicitudRef,
          viaRef,
          modalidadBajaRef,
          lugarEjecucionRef,
          domicilioRef,
          notasRef,
        ];
      }
      return [solicitanteRef, fechaEncargoRef, fechaSolicitudRef, viaRef, modalidadBajaRef, lugarEjecucionRef, domicilioRef, notasRef];
    }, [isStandaloneFull]);

    const focusNext = (currentRef) => {
      const idx = focusOrder.indexOf(currentRef);
      if (idx === -1) return;
      for (let i = idx + 1; i < focusOrder.length; i++) {
        const r = focusOrder[i];
        if (r && r.current && typeof r.current.focus === "function") {
          r.current.focus();
          return;
        }
      }
    };

    const makeEnterHandler = (fieldRef) => (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        focusNext(fieldRef);
      }
    };

    // ===== Inputs de fecha con calendario =====
    const [openFechaEncargoPicker, setOpenFechaEncargoPicker] = useState(false);
    const [openFechaSolicitudPicker, setOpenFechaSolicitudPicker] = useState(false);

    const fechaEncargoValue = Form.useWatch("fecha_encargo", form);
    const fechaSolicitudValue = Form.useWatch("fecha_solicitud", form);
    const lugarEjecucionWatch = Form.useWatch("lugar_ejecucion", form);

    const lugarOtroId = useMemo(() => {
      const opt = lugarEjecucionOptions.find((o) => (o.label || "").toLowerCase() === "otro");
      return opt?.value ?? null;
    }, [lugarEjecucionOptions]);

    // ============================
    // Razón social (selección única, como tribunal)
    // ============================
    const [rsSelectedValue, setRsSelectedValue] = useState(null);
    const [corresponsalDisabledNombre, setCorresponsalDisabledNombre] = useState("");

    const allRazonesSociales = useMemo(() => {
      const all = [];
      for (const [, bucket] of catalogEmpresasIndex.entries()) {
        all.push(...(bucket.razones || []));
      }
      return dedupRazones(all);
    }, [catalogEmpresasIndex]);

    const handleRazonSocialChange = (value) => {
      setRsSelectedValue(value ?? null);

      if (!value) {
        form.setFieldsValue({ empresa_razon_social_id: [], empresa_id: null });
        setCorresponsalDisabledNombre("");
        return;
      }

      const chosen = allRazonesSociales.find(
        (o) => String(o.value == null ? o.label : o.value) === String(value)
      );
      const empresaId = chosen?.empresa_id ?? null;
      form.setFieldsValue({ empresa_razon_social_id: [value], empresa_id: empresaId });

      if (empresaId != null) {
        const empresa = (catalogEmpresasItems || []).find(
          (e) => (e?.id_empresa ?? e?.id) === empresaId
        );
        if (empresa) {
          const nCorr = pickFirst(empresa, [
            "corresponsal_nombre", "nombre_corresponsal",
            "nombre_contacto", "contacto_nombre",
          ]);
          setCorresponsalDisabledNombre(nCorr || "");
        } else {
          setCorresponsalDisabledNombre("");
        }
      } else {
        setCorresponsalDisabledNombre("");
      }
    };


    const resetEmpresaSelection = () => {
      setClienteSource("search");
      setNuevoClienteData(null);
      setRsSelectedValue(null);
      setCorresponsalDisabledNombre("");

      form.setFieldsValue({
        rs_single_display: undefined,
        empresa_id: null,
        empresa_razon_social_id: [],
        empresa_razon_social_nombre: [],
        nuevo_cliente: null,
      });
    };


    const makeFechaInputHandler = (fieldName) => (e) => {
      let v = e.target.value.replace(/\D/g, "");
      let formatted = v;
      if (v.length > 2) formatted = v.slice(0, 2) + "/" + v.slice(2);
      if (v.length > 4) formatted = formatted.slice(0, 5) + "/" + formatted.slice(5);
      formatted = formatted.slice(0, 10);
      e.target.value = formatted;
      form.setFieldsValue({ [fieldName]: formatted });
    };
const makeFechaHoraInputHandler = (fieldName) => (e) => {
  let v = e.target.value.replace(/\D/g, "");
  // ddmmyyyyhhmm (máximo 12 dígitos)
  v = v.slice(0, 12);

  let out = v;
  if (v.length > 2) out = v.slice(0, 2) + "/" + v.slice(2);
  if (v.length > 4) out = out.slice(0, 5) + "/" + out.slice(5);
  if (v.length > 8) out = out.slice(0, 10) + " " + out.slice(10);
  if (v.length > 10) out = out.slice(0, 13) + ":" + out.slice(13);

  // limita a "DD/MM/YYYY HH:MM" => 16 chars
  out = out.slice(0, 16);

  e.target.value = out;
  form.setFieldsValue({ [fieldName]: out });
};

    const handleFechaEncargoCalendarChange = (value) => {
      const formatted = value ? value.format("DD/MM/YYYY") : "";
      form.setFieldsValue({ fecha_encargo: formatted });
    };

    const handleFechaSolicitudCalendarChange = (value) => {
      const formatted = value ? value.format("DD/MM/YYYY") : "";
      form.setFieldsValue({ fecha_solicitud: formatted });
    };

    /* =========================
       Standalone FULL: empresas/razones dinámicas (sin cambios de tu lógica)
       ========================= */
    const [empresaCustomNames, setEmpresaCustomNames] = useState([]);
    const [empresaNewName, setEmpresaNewName] = useState("");
    const empresaInputRef = useRef(null);

    const [razonCustomByEmpresa, setRazonCustomByEmpresa] = useState({});
    const [razonNewName, setRazonNewName] = useState("");
    const razonInputRef = useRef(null);

    const empresaNombreWatch = Form.useWatch("empresa_nombre", form);
    const clienteDirectoWatch = Form.useWatch("cliente_directo", form);

    const empresaNombreOptions = useMemo(() => {
      const extra = empresaCustomNames.map((n) => ({ label: n, value: n }));
      return [...catalogEmpresaNombreOptionsBase, ...extra];
    }, [catalogEmpresaNombreOptionsBase, empresaCustomNames]);

    const razonSocialOptionsBase = useMemo(() => {
      if (!empresaNombreWatch) return [];
      const bucket = catalogEmpresasIndex.get(empresaNombreWatch);
      if (!bucket) return [];
      return bucket.razones || [];
    }, [empresaNombreWatch, catalogEmpresasIndex]);

    const razonSocialOptions = useMemo(() => {
      const extra = razonCustomByEmpresa[empresaNombreWatch] || [];
      return dedupRazones([...(razonSocialOptionsBase || []), ...extra]);
    }, [razonSocialOptionsBase, razonCustomByEmpresa, empresaNombreWatch]);

    const empresaEsNueva = useMemo(() => {
      const empresaNombreVal = form.getFieldValue("empresa_nombre") || "";
      if (!empresaNombreVal) return false;

      const baseExists = (catalogEmpresaNombreOptionsBase || []).some(
        (o) => (o.value || "").toLowerCase() === empresaNombreVal.toLowerCase()
      );
      const extraExists = (empresaCustomNames || []).some((n) => n.toLowerCase() === empresaNombreVal.toLowerCase());
      return !baseExists && extraExists;
    }, [catalogEmpresaNombreOptionsBase, empresaCustomNames, form, empresaNombreWatch]);

    useEffect(() => {
      if (!isStandaloneFull) return;
      if (isClienteDesdeModal) return;

      const empresaNombreVal = form.getFieldValue("empresa_nombre") || "";

      if (!empresaNombreVal) {
        form.setFieldsValue({
          cliente_directo: true,
          corresponsal_nombre: undefined,
          corresponsal_celular: undefined,
          corresponsal_correo: undefined,
        });
        return;
      }

      if (empresaEsNueva) {
        const directNow = !!form.getFieldValue("cliente_directo");
        if (directNow) {
          form.setFieldsValue({
            cliente_directo: true,
            corresponsal_nombre: undefined,
            corresponsal_celular: undefined,
            corresponsal_correo: undefined,
          });
        }
        return;
      }

      const nombreNormalizado = collapseSpaces(empresaNombreVal).toLowerCase();
      const bucket = catalogEmpresasIndex.get(empresaNombreVal);
      const rawList = bucket?.raw || [];

      const empresaMatch =
        rawList.find((e) => collapseSpaces(pickCompanyName(e)).toLowerCase() === nombreNormalizado) ||
        (catalogEmpresasItems || []).find((e) => collapseSpaces(pickCompanyName(e)).toLowerCase() === nombreNormalizado);

      if (!empresaMatch) {
        form.setFieldsValue({
          cliente_directo: true,
          corresponsal_nombre: undefined,
          corresponsal_celular: undefined,
          corresponsal_correo: undefined,
        });
        return;
      }

      const esDirecto = coerceBool01(empresaMatch?.cliente_directo, true);

      const nCorr = pickFirst(empresaMatch, [
        "corresponsal_nombre",
        "nombre_corresponsal",
        "nombre_corresponsal_empresa",
        "nombre_contacto",
        "contacto_nombre",
      ]);

      const cCorr = pickFirst(empresaMatch, [
        "corresponsal_celular",
        "celular_corresponsal",
        "telefono_corresponsal",
        "telefono_contacto",
        "contacto_telefono",
      ]);

      const eCorr = pickFirst(empresaMatch, [
        "corresponsal_correo",
        "correo_corresponsal",
        "email_corresponsal",
        "correo_contacto",
        "email_contacto",
        "contacto_email",
      ]);

      form.setFieldsValue({
        cliente_directo: esDirecto,
        corresponsal_nombre: esDirecto ? undefined : nCorr || undefined,
        corresponsal_celular: esDirecto ? undefined : cCorr || undefined,
        corresponsal_correo: esDirecto ? undefined : eCorr || undefined,
      });
    }, [isStandaloneFull, empresaNombreWatch, empresaEsNueva, catalogEmpresasIndex, catalogEmpresasItems, form, isClienteDesdeModal]);

    /* =========================
       Inicialización (create/edit)
       ========================= */
    const didInitRef = useRef(false);
    const lastAppliedKeyRef = useRef(null);

    const mappedInitialValues = useMemo(() => {
      const alreadyLooksDDMMYYYY = (s) => typeof s === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(s);
      const src = initialValues || {};

      const empleadosFromBackendArr = Array.isArray(src?.empleados) ? src.empleados : null;

      const empleadosIniciales =
        empleadosFromBackendArr && empleadosFromBackendArr.length
          ? empleadosFromBackendArr
              .map((e) => ({
                nombre_completo: collapseSpaces(e?.nombre_completo ?? e?.nombre ?? e?.solicitante_nombre ?? ""),
              }))
              .filter((e) => !!e.nombre_completo)
          : [
              {
                nombre_completo: collapseSpaces(src?.solicitante_nombre ?? src?.nombre_solicitante ?? ""),
              },
            ].filter((e) => !!e.nombre_completo);

      const fechaEncargoFromBackend = src?.fecha_encargo ?? "";
      const fechaSolicitudFromBackend = src?.fecha_solicitud ?? src?.fecha_solicitacion ?? "";
      const idCiudadMapped = src?.id_ciudad ?? null;

      const empresaNombre = src?.nombre_empresa ?? src?.empresa_nombre ?? src?.cliente_nombre ?? "";
      const empresaId = src?.id_empresa ?? src?.empresa_id ?? null;

      const rsId = src?.id_razon_social ?? null;
      const rsNombre = src?.nombre_razon_social ?? "";

      const clienteDirecto = src?.cliente_directo != null ? !!src?.cliente_directo : null;

      const nCorr = src?.nombre_corresponsal ?? src?.corresponsal_nombre ?? "";
      const cCorr = src?.celular_corresponsal ?? src?.corresponsal_celular ?? "";
      const eCorr = src?.correo_corresponsal ?? src?.corresponsal_correo ?? "";

      const OLD_MODALIDAD_SLUG = { privado: 1, ratificado: 2 };
      const OLD_LUGAR_SLUG = { centro_trabajo: 1, despacho: 2, centro_conciliacion: 3, otro: 4 };

      const viaFromSrc = src?.via_tramite ?? undefined;
      let modalidadMapped;
      if (viaFromSrc != null && viaFromSrc !== "") {
        const n = Number(viaFromSrc);
        modalidadMapped = Number.isFinite(n) && n > 0 ? n : (OLD_MODALIDAD_SLUG[viaFromSrc] ?? undefined);
      }

      const lugarFromSrc = src?.origen_privado ?? null;
      let lugarEjecucionMapped;
      let lugarEjecucionOtroMapped;
      if (lugarFromSrc != null && String(lugarFromSrc).trim() !== "") {
        const n = Number(lugarFromSrc);
        if (Number.isFinite(n) && n > 0) {
          lugarEjecucionMapped = n;
        } else {
          const mappedId = OLD_LUGAR_SLUG[lugarFromSrc];
          if (mappedId) {
            lugarEjecucionMapped = mappedId;
          } else {
            lugarEjecucionMapped = 4;
            lugarEjecucionOtroMapped = String(lugarFromSrc);
          }
        }
      }

      return {
        ciudad: idCiudadMapped,
        empresa_nombre: empresaNombre || undefined,
        empresa_id: empresaId,
        empresa_razon_social_id: rsId ? [rsId] : [],
        empresa_razon_social_nombre: rsNombre ? [rsNombre] : [],
        cliente_directo: clienteDirecto == null ? true : !!clienteDirecto,
        corresponsal_nombre: nCorr || undefined,
        corresponsal_celular: cCorr || undefined,
        corresponsal_correo: eCorr || undefined,

        empleados: empleadosIniciales.length ? empleadosIniciales : [{ nombre_completo: "" }],

        fecha_encargo: alreadyLooksDDMMYYYY(fechaEncargoFromBackend) ? fechaEncargoFromBackend : isoToDDMMYYYY(fechaEncargoFromBackend),
        fecha_solicitud: alreadyLooksDDMMYYYY(fechaSolicitudFromBackend) ? fechaSolicitudFromBackend : isoToDDMMYYYY(fechaSolicitudFromBackend),

        modalidad_baja: modalidadMapped,
        lugar_ejecucion: lugarEjecucionMapped,
        lugar_ejecucion_otro: lugarEjecucionOtroMapped,
        notas: src?.notas ?? "",
        domicilio_ejecucion: src?.domicilio_ejecucion ?? "",

        nuevo_cliente: null,
      };
    }, [initialValues]);

    useEffect(() => {
      if (!isEdit) {
        if (didInitRef.current) return;

        setClienteSource("search");
        setNuevoClienteData(null);

        form.setFieldsValue({
          ciudad: undefined,
          empresa_nombre: undefined,
          empresa_id: null,
          empresa_razon_social_id: [],
          empresa_razon_social_nombre: [],
          cliente_directo: true,
          corresponsal_nombre: undefined,
          corresponsal_celular: undefined,
          corresponsal_correo: undefined,

          empleados: [{ nombre_completo: "" }],

          fecha_encargo: "",
          fecha_solicitud: "",
          modalidad_baja: undefined,
          lugar_ejecucion: undefined,
          lugar_ejecucion_otro: undefined,
          domicilio_ejecucion: "",
          notas: "",
          nuevo_cliente: null,

          ...mappedInitialValues,
        });

        const rsIds0 = mappedInitialValues?.empresa_razon_social_id || [];
        const rsVal0 = rsIds0.length ? String(rsIds0[0]) : null;
        setRsSelectedValue(rsVal0);
        if (rsVal0) form.setFieldsValue({ rs_single_display: rsVal0 });
        setCorresponsalDisabledNombre(mappedInitialValues?.corresponsal_nombre || "");

        setDocumentsMap({ documento: [] });
        setDocErrors({});
        didInitRef.current = true;
        lastAppliedKeyRef.current = "CREATE";
        return;
      }

      const editKey = String(idDesvinculacion ?? "");
      if (!editKey) return;
      if (lastAppliedKeyRef.current === editKey && didInitRef.current) return;

      setClienteSource("search");
      setNuevoClienteData(null);

      form.setFieldsValue({
        ciudad: undefined,
        empresa_id: null,
        empresa_razon_social_id: [],
        empresa_razon_social_nombre: [],
        rs_single_display: undefined,
        fecha_encargo: "",
        fecha_solicitud: "",
        modalidad_baja: undefined,
        lugar_ejecucion: undefined,
        lugar_ejecucion_otro: undefined,
        domicilio_ejecucion: "",
        notas: "",
        nuevo_cliente: null,

        ...mappedInitialValues,
      });

      const rsIds1 = mappedInitialValues?.empresa_razon_social_id || [];
      const rsVal1 = rsIds1.length ? String(rsIds1[0]) : null;
      setRsSelectedValue(rsVal1);
      if (rsVal1) form.setFieldsValue({ rs_single_display: rsVal1 });
      setCorresponsalDisabledNombre(mappedInitialValues?.corresponsal_nombre || "");

      setDocErrors({});
      didInitRef.current = true;
      lastAppliedKeyRef.current = editKey;
    }, [mappedInitialValues, form, isEdit, idDesvinculacion]);

    /* ======================= Upload Documentos (varios) ======================= */
    const beforeUploadDoc = (key) => (file) => {
      setDocErrors((prev) => ({ ...prev, [key]: "" }));

      const isOkType =
        file.type === "application/pdf" ||
        file.type === "image/png" ||
        file.type === "image/jpeg" ||
        /\.pdf$/i.test(file.name) ||
        /\.(png|jpe?g)$/i.test(file.name);

      if (!isOkType) {
        const msg = "Solo PDF/JPG/PNG.";
        setDocErrors((prev) => ({ ...prev, [key]: msg }));
        notify.error({ message: msg, placement: "bottomRight" });
        return Upload.LIST_IGNORE;
      }

      const maxMB = 25;
      if (file.size > maxMB * 1024 * 1024) {
        const msg = `El archivo supera ${maxMB} MB.`;
        setDocErrors((prev) => ({ ...prev, [key]: msg }));
        notify.error({ message: msg, placement: "bottomRight" });
        return Upload.LIST_IGNORE;
      }

      setDocumentsMap((prev) => ({
        ...prev,
        [key]: [...(prev[key] || []), file],
      }));
      return false;
    };

    const removeDoc = (key, uid) => {
      setDocumentsMap((prev) => ({
        ...prev,
        [key]: (prev[key] || []).filter((f) => f.uid !== uid),
      }));
      setDocErrors((prev) => ({ ...prev, [key]: "" }));
    };

    const DocBlock = ({ label, keyName }) => {
      const filesArr = documentsMap[keyName] || [];

      return (
        <div className="expediente-doc-block">
          {label ? <Text strong>{label}</Text> : null}

          <Dragger
            multiple
            accept=".pdf,.png,.jpg,.jpeg"
            beforeUpload={beforeUploadDoc(keyName)}
            showUploadList={false}
            style={{ marginTop: 8 }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Haz clic o arrastra aquí los archivos</p>
            <p className="ant-upload-hint">Puedes agregar varios. Se enviarán al presionar "Guardar".</p>
          </Dragger>

          {filesArr.length > 0 ? (
            <div className="desvinc-doc-list">
              {filesArr.map((f) => (
                <div className="desvinc-doc-item" key={f.uid}>
                  <i className="fa-solid fa-paperclip" />
                  <span className="desvinc-doc-item-name">{f.name}</span>
                  <Button
                    danger
                    type="text"
                    size="small"
                    onClick={() => removeDoc(keyName, f.uid)}
                  >
                    Quitar
                  </Button>
                </div>
              ))}
            </div>
          ) : null}

          {docErrors[keyName] ? (
            <Alert style={{ marginTop: 8 }} type="error" showIcon message={docErrors[keyName]} />
          ) : null}
        </div>
      );
    };

    /* ======================= Validaciones ======================= */
    const rules = {
      ciudad: [{ required: isStandaloneFull, message: "Selecciona la ciudad" }],
      empresa_nombre: [{ required: isStandaloneFull, message: "Selecciona el cliente" }],
      empresa_razon_social_id: [{ required: isStandaloneFull, message: "Selecciona razón social" }],
      fecha_encargo: [{ required: true, message: "Selecciona la fecha" }],
      fecha_solicitud: [{ required: true, message: "Selecciona la fecha" }],
      modalidad_baja: [{ required: true, message: "Selecciona la modalidad de baja" }],
      lugar_ejecucion: [{ required: true, message: "Selecciona el lugar de ejecución" }],
      lugar_ejecucion_otro: [{ required: true, message: "Especifica el lugar de ejecución" }],
    };

    /* ======================= Guardado ======================= */
    const buildAndSend = async (values) => {
      // ========== Modalidad + Lugar ==========
      const via_tramite_from_cascader = values.modalidad_baja || null;
      const lugar_raw = values.lugar_ejecucion || null;
      const origen_privado_from_cascader =
        lugarOtroId !== null && lugar_raw === lugarOtroId
          ? collapseSpaces(values.lugar_ejecucion_otro || "") || null
          : lugar_raw;

      // ========== Fechas ==========
      const fechaEncargoNorm = normalizeFechaDDMMYYYY_to_YYYYMMDD(values.fecha_encargo);
      const fechaBajaNorm = normalizeFechaDDMMYYYYHHMM_to_YYYYMMDD_HHMMSS(values.fecha_solicitud);


      const empleadosArrRaw = Array.isArray(values.empleados) ? values.empleados : [];
      const empleadosNormalized = empleadosArrRaw.map((e, idx) => {
        const raw = e?.nombre_completo || "";
        const s1 = sanitizeByField("solicitante", raw);
        const s2 = collapseSpaces(s1);
        return { nombre_completo: s2 ? s2 : `Empleado ${idx + 1}` };
      });

      const empleadosFinal =
        empleadosNormalized.length > 0 ? empleadosNormalized : [{ nombre_completo: "Empleado 1" }];

      const clean = {
        empleados: empleadosFinal,
        fecha_encargo: fechaEncargoNorm,
        fecha_solicitud: fechaBajaNorm,
        via_tramite: via_tramite_from_cascader,
        lugar_ejecucion: lugar_raw,
        origen_privado: origen_privado_from_cascader,
        domicilio_ejecucion: toNullIfEmptyEffective(sanitizeByField("domicilio_ejecucion", values.domicilio_ejecucion)) ?? null,
        notas: toNullIfEmptyEffective(sanitizeByField("notas", values.notas)) ?? null,
      };

      // ========= IDs efectivos (solo si NO viene del modal) =========
      const idCiudadEffective =
        (isStandaloneFull ? coerceIntOrNull(values.ciudad) : null) ??
        coerceIntOrNull(idCiudad) ??
        coerceIntOrNull(initialValues?.id_ciudad) ??
        null;

      const idEmpresaEffective =
        (isStandaloneFull ? coerceIntOrNull(values.empresa_id) : null) ??
        coerceIntOrNull(ctxEmpresaId) ??
        coerceIntOrNull(initialValues?.id_empresa) ??
        coerceIntOrNull(initialValues?.empresa_id) ??
        null;

      const idRazonSocialEffective =
        (isStandaloneFull ? coerceIntOrNull((values.empresa_razon_social_id || [])[0]) : null) ??
        coerceIntOrNull((ctxRazonSocialIds || [])[0]) ??
        coerceIntOrNull(initialValues?.id_razon_social) ??
        null;

      const requiredErrors = [];
      if (!clean.empleados || !clean.empleados.length) requiredErrors.push("empleados");
      if (!clean.fecha_encargo) requiredErrors.push("fecha_encargo");
      if (!clean.fecha_solicitud) requiredErrors.push("fecha_solicitud");
      if (!clean.via_tramite) requiredErrors.push("modalidad_baja");
      if (!clean.lugar_ejecucion) requiredErrors.push("lugar_ejecucion");
      if (lugarOtroId !== null && clean.lugar_ejecucion === lugarOtroId && !clean.origen_privado) requiredErrors.push("lugar_ejecucion_otro");

      // ids obligatorios SOLO cuando es por búsqueda (empresa existente)
      if (!isClienteDesdeModal) {
        if (!idCiudadEffective) requiredErrors.push("ciudad");
        if (!idEmpresaEffective) requiredErrors.push("empresa_id");
        if (!idRazonSocialEffective) requiredErrors.push("id_razon_social");
      } else {
        // si viene del modal, al menos ciudad (si aplica tu flujo) debería existir
        if (!idCiudadEffective) requiredErrors.push("ciudad");
      }

      let ctx = {};
      if (isStandaloneFull) {
        ctx = {
          origen_captura: "standalone_full",
          isClienteNuevo: isClienteDesdeModal ? true : !!empresaEsNueva,
        };
      } else {
        ctx = { origen_captura: variant || "context_view", isClienteNuevo: isClienteDesdeModal ? true : false };
      }

      if (requiredErrors.length) {
        notify.error({
          message: "Faltan campos obligatorios: " + requiredErrors.join(", "),
          placement: "bottomRight",
        });
        return;
      }

      // ========= Agenda event fields (1.2 title, 1.4 location, 1.5 description) =========
      const _modalidadLabelMap = Object.fromEntries(
        modalidadBajaOptions.map((o) => [o.value, o.label])
      );
      const _lugarLabelMap = Object.fromEntries(
        lugarEjecucionOptions
          .filter((o) => (o.label || "").toLowerCase() !== "otro")
          .map((o) => [o.value, o.label])
      );
      const modalidadLabel = _modalidadLabelMap[via_tramite_from_cascader] || String(via_tramite_from_cascader || "") || "—";
      const lugarFinalLabel =
        lugarOtroId !== null && lugar_raw === lugarOtroId
          ? collapseSpaces(values.lugar_ejecucion_otro || "") || "Otro"
          : _lugarLabelMap[lugar_raw] || String(lugar_raw || "") || "—";
      const numTrab = empleadosFinal.length;
      const nombresTrab = empleadosFinal
        .map((e) => collapseSpaces(e.nombre_completo || ""))
        .filter(Boolean)
        .join(", ");
      const agendaDescripcion = [
        `Modalidad: ${modalidadLabel}`,
        `Lugar: ${lugarFinalLabel}`,
        clean.domicilio_ejecucion ? `Domicilio de ejecución: ${clean.domicilio_ejecucion}` : null,
        `Trabajadores involucrados: ${numTrab}${nombresTrab ? " — " + nombresTrab : ""}`,
        `Documentos adjuntos: ${(documentsMap.documento || []).length}`,
      ].filter(Boolean).join("\n");

      // ========= payload =========
      const backendPayloadBase = {
        ...ctx,

        empleados: clean.empleados,
        solicitante_nombre: clean.empleados?.[0]?.nombre_completo || null,

        fecha_encargo: clean.fecha_encargo,
        fecha_solicitud: clean.fecha_solicitud,

        via_tramite: clean.via_tramite,
        id_modalidad_baja: via_tramite_from_cascader ?? null,
        id_lugar_ejecucion: lugar_raw ?? null,
        lugar_ejecucion: lugarFinalLabel !== "—" ? lugarFinalLabel : null,
        origen_privado: clean.origen_privado,

        domicilio_ejecucion: clean.domicilio_ejecucion,
        notas: clean.notas,

        // Agenda overrides: label (1.2), location (1.4), structured description (1.5)
        agenda_titulo: "Baja programada",
        agenda_descripcion: agendaDescripcion,
        agenda_ubicacion: lugarFinalLabel !== "—" ? lugarFinalLabel : null,
      };

      const backendPayload = !isClienteDesdeModal
        ? {
            ...backendPayloadBase,
            id_ciudad: idCiudadEffective,
            id_empresa: idEmpresaEffective,
            id_razon_social: idRazonSocialEffective,
          }
        : {
            ...backendPayloadBase,
            id_ciudad: idCiudadEffective,
            id_empresa: null,
            id_razon_social: null,

            // NUEVO: datos para crear cliente/empresa desde modal
            nuevo_cliente: {
              ...(nuevoClienteData || {}),
            },
          };

      const filesMap = {};
      if ((documentsMap.documento || []).length) filesMap.documento = documentsMap.documento;

      try {
        setSubmitting(true);

        if (typeof onSubmit === "function") {
          await onSubmit(backendPayload, filesMap);
          notify.success({
            message: isEdit ? "Desvinculación actualizada" : "Desvinculación creada",
            description: "Se guardó correctamente.",
            placement: "bottomRight",
          });
          onSaved?.(backendPayload, filesMap);
          return;
        }

        if (isEdit) {
       
          if (!idDesvinculacion) {
            notify.error({ message: "Falta idDesvinculacion para editar.", placement: "bottomRight" });
            return;
          }

          const res = await dispatch(
            actionDesvinculacionUpdateById(idDesvinculacion, backendPayload, () => onSaved?.(), filesMap)
          );

          notify.success({
            message: "Desvinculación actualizada",
            description: "Los cambios se guardaron correctamente.",
            placement: "bottomRight",
          });

          onSaved?.(backendPayload, filesMap, res);
        } else {
          const res = await dispatch(actionDesvinculacionCreate(backendPayload, () => onSaved?.(), filesMap));

          notify.success({
            message: "Desvinculación creada",
            description: "Se guardó correctamente.",
            placement: "bottomRight",
          });

          const newId = res?.id_desvinculacion ?? res?.id ?? res?.insertId ?? null;
          if (newId) navigate(`/materias/laboral/desvinculaciones/${newId}`);

          onSaved?.(backendPayload, filesMap, res);
        }
      } finally {
        setSubmitting(false);
      }
    };

    const handleSaveClick = async () => {
      try {
        await form.validateFields();
        const values = form.getFieldsValue(true);
        await buildAndSend(values);
      } catch (err) {
        const errorFields = err?.errorFields || [];
        if (errorFields.length) {
          notify.error({ message: "Faltan campos obligatorios.", placement: "bottomRight", duration: 3 });
          form.scrollToField(errorFields[0].name, { block: "center" });
          return;
        }

      
      }
    };

    useImperativeHandle(ref, () => ({
      submit: () => handleSaveClick(),
      validate: () => form.validateFields(),
      getValues: () => form.getFieldsValue(true),
      focusFirst: () => {
        const first = isStandaloneFull ? ciudadRef : solicitanteRef;
        if (first.current && typeof first.current.focus === "function") first.current.focus();
      },
    }));

    useEffect(() => {
      const arr = Array.isArray(empleadosWatch) ? empleadosWatch : [];
      const n = arr.length || 1;
      if (empleadosCount !== n) setEmpleadosCount(n);
    }, [empleadosWatch]);

    const renderCardClienteSearch = () => (
      <section className="oa-card">
        <div className="oa-card-head oa-head-split">
          <h2 className="oa-card-title">
            <span className="oa-card-icon">
              <i className="fa-solid fa-user" />
            </span>
            Datos del cliente
          </h2>
          <Button
            icon={<PlusOutlined />}
            onClick={() => setOpenNuevoCliente(true)}
          >
            Crear nuevo cliente
          </Button>
        </div>

        <div className="oa-card-body">
        <Row gutter={[12, 12]}>
          <Col xs={24} md={16}>
            <Form.Item
              label="RAZÓN SOCIAL"
              name="rs_single_display"
              rules={[{ required: isStandaloneFull && !isClienteDesdeModal, message: "Selecciona la razón social" }]}
            >
              <Select
                ref={razonRef}
                value={rsSelectedValue}
                placeholder="Seleccionar razón social"
                showSearch
                allowClear
                optionFilterProp="label"
                options={allRazonesSociales.map((o) => ({
                  label: o.label,
                  value: String(o.value == null ? o.label : o.value),
                }))}
                onChange={handleRazonSocialChange}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={8}>
            <Form.Item label="CORRESPONSAL">
              <Input disabled value={corresponsalDisabledNombre || ""} placeholder="—" />
            </Form.Item>
          </Col>
        </Row>
        </div>
      </section>
    );

    const renderCardClienteDesdeModal = () => {
      const nc = nuevoClienteData || {};
      const esDirecto = !!nc?.cliente_directo;

      return (
        <section className="oa-card">
          <div className="oa-card-head oa-head-split">
            <h2 className="oa-card-title">
              <span className="oa-card-icon">
                <i className="fa-solid fa-user" />
              </span>
              Cliente nuevo
              <Tag color="blue" style={{ marginLeft: 6 }}>{esDirecto ? "Directo" : "Corresponsal"}</Tag>
            </h2>
            <Button onClick={() => resetEmpresaSelection()}>
              Cambiar a búsqueda
            </Button>
          </div>

          <div className="oa-card-body">
          <Row gutter={[12, 12]}>
            <Col xs={24} md={12}>
              <Form.Item label="Nombre Comercial">
                <Input disabled value={nc?.cliente_nombre || ""} placeholder="—" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Razón social">
                <Input disabled value={nc?.razon_social || ""} placeholder="—" />
              </Form.Item>
            </Col>

            {esDirecto ? (
              <>
                <Col xs={24} md={12}>
                  <Form.Item label="Contacto - Nombre">
                    <Input disabled value={nc?.nombre_contacto || ""} placeholder="—" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item label="Contacto - Correo">
                    <Input disabled value={nc?.correo_contacto || ""} placeholder="—" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item label="Contacto - Celular">
                    <Input disabled value={nc?.celular_contacto || ""} placeholder="—" />
                  </Form.Item>
                </Col>
              </>
            ) : (
              <>
                <Col xs={24} md={12}>
                  <Form.Item label="Corresponsal - Despacho">
                    <Input disabled value={nc?.corresponsal_nombre || ""} placeholder="—" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Corresponsal - Abogado">
                    <Input disabled value={nc?.corresponsal_nombre_abogado || ""} placeholder="—" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Corresponsal - Correo">
                    <Input disabled value={nc?.corresponsal_correo || ""} placeholder="—" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item label="Corresponsal - Celular">
                    <Input disabled value={nc?.corresponsal_celular || ""} placeholder="—" />
                  </Form.Item>
                </Col>
              </>
            )}
          </Row>
          </div>
        </section>
      );
    };

    return (
      <>
        <div className="oa-exp-page">

          {showTopActions ? (
            <section className="oa-exp-topbar">
              <div className="oa-exp-topbar-left">
                <div className="oa-exp-title">
                  {isEdit ? "Editando Desvinculación" : "Creando Desvinculación"}
                </div>
                <div className="oa-exp-subtitle">Desvinculaciones · Materia laboral</div>
              </div>
              <div className="oa-exp-topbar-right">
                {onCancel ? (
                  <Button className="oa-exp-btn oa-exp-btn-ghost" onClick={onCancel} disabled={submitting}>
                    Cancelar
                  </Button>
                ) : null}
                <Button
                  type="primary"
                  className="oa-exp-btn"
                  loading={submitting}
                  onClick={handleSaveClick}
                  icon={
                    isEdit ? (
                      <svg viewBox="0 0 448 512" width="13" height="13" fill="currentColor" aria-hidden="true">
                        <path d="M64 0C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V154.5c0-17-6.7-33.3-18.7-45.3L400 60.7C388 48.7 371.7 42 354.5 42H64zm0 96c0-17.7 14.3-32 32-32H288c17.7 0 32 14.3 32 32v64c0 17.7-14.3 32-32 32H96c-17.7 0-32-14.3-32-32V96zM224 288a64 64 0 1 1 0 128 64 64 0 1 1 0-128z" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 448 512" width="13" height="13" fill="currentColor" aria-hidden="true">
                        <path d="M432 256c0 17.69-14.33 32.01-32 32.01H256v144c0 17.69-14.33 31.99-32 31.99s-32-14.3-32-31.99v-144H48c-17.67 0-32-14.32-32-32.01s14.33-31.99 32-31.99H192v-144c0-17.69 14.33-32.01 32-32.01s32 14.32 32 32.01v144h144C417.7 224 432 238.3 432 256z" />
                      </svg>
                    )
                  }
                >
                  {isEdit ? "Guardar cambios" : "Crear desvinculación"}
                </Button>
              </div>
            </section>
          ) : null}

          <main className="oa-exp-main">
          <Spin spinning={submitting} tip={isEdit ? "Guardando cambios..." : "Guardando..."}>
            <Form form={form} layout="vertical" className="desvinc-ant-form">

  {isStandaloneFull ? (
    <section className="oa-card">
      <div className="oa-card-head">
        <h2 className="oa-card-title">
          <span className="oa-card-icon">
            <i className="fa-solid fa-location-dot" />
          </span>
          Ubicación
        </h2>
      </div>
      <div className="oa-card-body">

      <Row gutter={[12, 12]}>
        <Col xs={24} md={12}>
          <Form.Item label="ESTADO">
            <Select
              value={selectedEstadoId}
              options={catalogEstadoOptions}
              placeholder="Selecciona"
              allowClear
              showSearch
              optionFilterProp="label"
              onChange={(v) => {
                const n = v != null ? Number(v) : null;
                setSelectedEstadoId(Number.isFinite(n) ? n : null);
              }}
            />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item label="CIUDAD" name="ciudad" rules={rules.ciudad}>
  <Select
    ref={ciudadRef}
    placeholder={selectedEstadoId ? "Selecciona" : "Primero selecciona un estado"}
    disabled={!selectedEstadoId}
    options={ciudadOptionsFiltered}
    allowClear
    showSearch
    optionFilterProp="label"
  />
</Form.Item>

        </Col>
      </Row>
      </div>
    </section>
  ) : null}

  

                {/* ========= CARD 1: CLIENTE ========= */}
                {isClienteDesdeModal ? renderCardClienteDesdeModal() : renderCardClienteSearch()}

                {/* ── Desvinculación ── */}
                <section className="oa-card">
                  <div className="oa-card-head">
                    <h2 className="oa-card-title">
                      <span className="oa-card-icon">
                        <i className="fa-solid fa-scissors" />
                      </span>
                      Datos de la desvinculación
                    </h2>
                  </div>
                  <div className="oa-card-body">

                  <Row gutter={[16, 12]}>
                    {/* ===== Columna 1: EMPLEADOS ===== */}
                    <Col xs={24} md={10} className="empleados-col">
                      <div className="empleados-header">
                        <span className="empleados-title-text">EMPLEADOS</span>
                        <span className="empleados-count-badge">{empleadosCount}</span>
                      </div>

                      <Form.List name="empleados">
                        {(fields, { add, remove }) => (
                          <>
                            <div className="empleados-scroll">
                              {fields.map((field, idx) => (
                                <Row key={field.key} gutter={[8, 8]} align="middle">
                                  <Col flex="auto">
                                    <Form.Item
                                      {...field}
                                      name={[field.name, "nombre_completo"]}
                                      style={{ marginBottom: 8 }}
                                      rules={[
                                        {
                                          pattern: /^[A-Za-zÁÉÍÓÚÜÑáéíóúüñ'.\- ]{0,120}$/,
                                          message: "Solo letras, espacios y ' . -",
                                        },
                                      ]}
                                    >
                                      <Input
                                        ref={idx === 0 ? solicitanteRef : null}
                                        placeholder={`Empleado ${idx + 1} (nombre completo)`}
                                        allowClear
                                        onKeyDown={idx === 0 ? makeEnterHandler(solicitanteRef) : undefined}
                                        onChange={(e) => {
                                          const clean = sanitizeByFieldLive("solicitante", e.target.value);
                                          if (clean !== e.target.value) {
                                            const cur = form.getFieldValue("empleados") || [];
                                            const next = Array.isArray(cur) ? [...cur] : [];
                                            next[idx] = { ...(next[idx] || {}), nombre_completo: clean };
                                            form.setFieldsValue({ empleados: next });
                                          }
                                        }}
                                      />
                                    </Form.Item>
                                  </Col>

                                  <Col>
                                    <Button
                                      danger
                                      size="small"
                                      onClick={() => remove(field.name)}
                                      disabled={fields.length <= 1}
                                      style={{ marginBottom: 8 }}
                                    >
                                      Quitar
                                    </Button>
                                  </Col>
                                </Row>
                              ))}
                            </div>

                            <Button
                              icon={<PlusOutlined />}
                              onClick={() => add({ nombre_completo: "" })}
                              style={{ marginTop: 8, width: "100%" }}
                            >
                              Agregar empleado
                            </Button>
                          </>
                        )}
                      </Form.List>
                    </Col>

                    {/* ===== Columna 2: RESTO DE DATOS ===== */}
                    <Col xs={24} md={14}>
                      <Row gutter={[12, 12]}>
                        <Col xs={24} md={12}>
                          <Form.Item label="FECHA DE SOLICITUD" name="fecha_encargo" rules={rules.fecha_encargo}>
                            <div style={{ position: "relative" }}>
                              <Input
                                ref={fechaEncargoRef}
                                placeholder="DD/MM/YYYY"
                                maxLength={10}
                                value={fechaEncargoValue || ""}
                                onChange={makeFechaInputHandler("fecha_encargo")}
                                onKeyDown={makeEnterHandler(fechaEncargoRef)}
                                suffix={
                                  <CalendarOutlined
                                    style={{ cursor: "pointer", color: "#0A539A" }}
                                    onClick={() => setOpenFechaEncargoPicker(true)}
                                  />
                                }
                              />

                              <ScrollDatePicker
                                open={openFechaEncargoPicker}
                                onOpenChange={setOpenFechaEncargoPicker}
                                format="DD/MM/YYYY"
                                value={fechaEncargoValue ? dayjs(fechaEncargoValue, "DD/MM/YYYY", true) : null}
                                onChange={(value) => {
                                  const formatted = value ? value.format("DD/MM/YYYY") : "";
                                  form.setFieldsValue({ fecha_encargo: formatted });
                                }}
                                style={{
                                  position: "absolute",
                                  inset: 0,
                                  opacity: 0,
                                  width: "100%",
                                  height: "100%",
                                }}
                              />

                            </div>
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                          <Form.Item label="FECHA DE LA BAJA" name="fecha_solicitud" rules={rules.fecha_solicitud}>
                            <div style={{ position: "relative" }}>
                              <Input
                                ref={fechaSolicitudRef}
                             
               
                                value={fechaSolicitudValue || ""}
                               onChange={makeFechaHoraInputHandler("fecha_solicitud")}
                                  placeholder="DD/MM/YYYY HH:MM"
                                  maxLength={16}
                                onKeyDown={makeEnterHandler(fechaSolicitudRef)}
                                suffix={
                                  <CalendarOutlined
                                    style={{ cursor: "pointer", color: "#0A539A" }}
                                    onClick={() => setOpenFechaSolicitudPicker(true)}
                                  />
                                }
                              />

                             <ScrollDatePicker
                                  open={openFechaSolicitudPicker}
                                  onOpenChange={setOpenFechaSolicitudPicker}
                                  showTime={{ format: "HH:mm" }}
                                  format="DD/MM/YYYY HH:mm"
                                  value={fechaSolicitudValue ? dayjs(fechaSolicitudValue, "DD/MM/YYYY HH:mm", true) : null}
                                  onChange={(value) => {
                                    const formatted = value ? value.format("DD/MM/YYYY HH:mm") : "";
                                    form.setFieldsValue({ fecha_solicitud: formatted });
                                  }}
                                  style={{
                                    position: "absolute",
                                    inset: 0,
                                    opacity: 0,
                                    width: "100%",
                                    height: "100%",
                                  }}
                                />

                            </div>
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                          <Form.Item
                            label="MODALIDAD DE BAJA"
                            name="modalidad_baja"
                            rules={rules.modalidad_baja}
                          >
                            <Select
                              ref={modalidadBajaRef}
                              options={modalidadBajaOptions}
                              placeholder="Selecciona"
                              allowClear
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={12}>
                          <Form.Item
                            label="LUGAR DE EJECUCIÓN"
                            name="lugar_ejecucion"
                            rules={rules.lugar_ejecucion}
                          >
                            <Select
                              ref={lugarEjecucionRef}
                              options={lugarEjecucionOptions}
                              placeholder="Selecciona"
                              allowClear
                              onChange={() => {
                                form.setFieldsValue({ lugar_ejecucion_otro: undefined });
                              }}
                            />
                          </Form.Item>
                        </Col>

                        {lugarOtroId !== null && lugarEjecucionWatch === lugarOtroId && (
                          <Col xs={24} md={24}>
                            <Form.Item
                              label="ESPECIFICA EL LUGAR"
                              name="lugar_ejecucion_otro"
                              rules={rules.lugar_ejecucion_otro}
                            >
                              <Input placeholder="Describe el lugar de ejecución" maxLength={200} />
                            </Form.Item>
                          </Col>
                        )}

                        <Col xs={24} md={24}>
                          <Form.Item label="DOMICILIO DE EJECUCIÓN (OPCIONAL)" name="domicilio_ejecucion">
                            <Input
                              ref={domicilioRef}
                              placeholder="Calle, número, colonia, ciudad — lugar físico donde se realiza la baja"
                              maxLength={500}
                              onKeyDown={makeEnterHandler(domicilioRef)}
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={24} md={24}>
                          <Form.Item label="NOTAS (OPCIONAL)" name="notas">
                            <Input.TextArea
                              ref={notasRef}
                              placeholder="Instrucciones adicionales, referencias internas, observaciones."
                              rows={3}
                              onKeyDown={makeEnterHandler(notasRef)}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Col>
                  </Row>

                  </div>
                </section>

                {/* ── Documento adjunto ── */}
                <section className="oa-card">
                  <div className="oa-card-head">
                    <h2 className="oa-card-title">
                      <span className="oa-card-icon">
                        <i className="fa-solid fa-paperclip" />
                      </span>
                      Documento adjunto
                    </h2>
                  </div>
                  <div className="oa-card-body">
                    <DocBlock label="" keyName="documento" />
                  </div>
                </section>

            </Form>
          </Spin>
          </main>
        </div>

        <style>{`
          /* ── Form fields ── */
          .desvinc-ant-form .ant-form-item {
            margin-bottom: 12px;
          }
          .desvinc-ant-form .ant-form-item-label > label {
            font-size: 11px;
            font-weight: 800;
            letter-spacing: 0.06em;
            color: #334155;
            text-transform: uppercase;
          }

          /* ── Upload doc block ── */
          .expediente-doc-block {
            border: 1px dashed #d9d9d9;
            border-radius: 8px;
            padding: 12px;
            background: #fafafa;
          }

          .desvinc-doc-list {
            display: flex;
            flex-direction: column;
            gap: 8px;
            margin-top: 10px;
          }
          .desvinc-doc-item {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 6px 10px;
            background: #ffffff;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
          }
          .desvinc-doc-item-name {
            flex: 1;
            font-size: 13px;
            color: #334155;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          /* ── Employees column divider ── */
          .empleados-col {
            border-right: 1px solid #e2e8f0;
            padding-right: 16px;
          }
          @media (max-width: 768px) {
            .empleados-col { border-right: none; padding-right: 0; }
          }

          /* ── Employee header ── */
          .empleados-header {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-bottom: 14px;
          }
          .empleados-title-text {
            font-size: 13px;
            font-weight: 800;
            letter-spacing: 0.06em;
            color: #0f172a;
            text-transform: uppercase;
          }
          .empleados-count-badge {
            background: #0A539A;
            color: #ffffff;
            border-radius: 999px;
            font-size: 12px;
            font-weight: 700;
            padding: 1px 10px;
            min-width: 28px;
            text-align: center;
          }

          /* ── Scroll list ── */
          .empleados-scroll {
            max-height: 360px;
            overflow-y: auto;
            padding-right: 4px;
          }
        `}</style>

        <ModalNuevoCliente
          open={openNuevoCliente}
          onCancel={() => setOpenNuevoCliente(false)}
          onCreated={(nuevo) => {
            // ===== activar modo modal =====
            setClienteSource("modal");
            setNuevoClienteData(nuevo);

            // ===== limpiar selección de RS =====
            setRsSelectedValue(null);
            setCorresponsalDisabledNombre(nuevo?.corresponsal_nombre || "");

            // ===== setFieldsValue mínimo, sin ids =====
            form.setFieldsValue({
              rs_single_display: undefined,
              empresa_id: null,
              empresa_razon_social_id: [],
              empresa_razon_social_nombre: [],
              nuevo_cliente: nuevo,
            });
          }}
        />
      </>
    );
  }
);

export default FormDesvinculaciones;
